import type { BreakdownReference } from '@/types/ai-session';
import type { CanonicalAISession } from '@/lib/ai-session/contracts/types';

export type AISessionAssetItem = {
  name: string;
  draftItemId?: string;
  description?: string;
};

export type AISessionAssetResult = {
  characters: AISessionAssetItem[];
  locations: AISessionAssetItem[];
  props: AISessionAssetItem[];
};

export type FollowUpMentionSuggestion = {
  reference?: BreakdownReference;
  key: string;
  label: string;
  value: string;
  description?: string;
};

export type LeadingMention = {
  token: string;
  text: string;
};

const ASSET_RESULT_GROUPS: Array<{
  key: keyof AISessionAssetResult;
  label: string;
}> = [
  { key: 'characters', label: '角色' },
  { key: 'locations', label: '场景' },
  { key: 'props', label: '道具' },
];

const FOLLOW_UP_MENTION_PATTERN = /(^|\s)@\[?([^\]\s@]*)$/;
// 只消费 token 后系统插入的一个分隔空格，其余空白属于用户正文。
const LEADING_MENTION_TOKEN_PATTERN = /^\s*(@\[[^\]]+\]) ?/;

export function getFollowUpMentionQuery(text: string): string | null {
  const match = text.match(FOLLOW_UP_MENTION_PATTERN);
  return match ? match[2] : null;
}

export function applyFollowUpMention(text: string, mention: string): string {
  if (!text.match(FOLLOW_UP_MENTION_PATTERN)) {
    return `${text}${text && !text.endsWith(' ') ? ' ' : ''}${mention}`;
  }
  return text.replace(
    FOLLOW_UP_MENTION_PATTERN,
    (_fullMatch, prefix: string) => `${prefix}${mention}`
  );
}

export function getLeadingMention(text: string): LeadingMention | null {
  const match = text.match(LEADING_MENTION_TOKEN_PATTERN);
  if (!match) return null;
  const token = match[1];
  return {
    token,
    text: text.slice(match[0].length),
  };
}

export const SHOT_SPLIT_COUNTS = [2, 3, 4, 5] as const;
export type ShotSplitCount = (typeof SHOT_SPLIT_COUNTS)[number];

const LEADING_SHOT_SPLIT_COMMAND_PATTERN = /^\/拆分(?:\[([2-5])镜\])?(?: |$)/;

export function isShotSplitCount(value: number): value is ShotSplitCount {
  return SHOT_SPLIT_COUNTS.some((count) => count === value);
}

export function formatShotSplitCommand(count: ShotSplitCount): string {
  return `/拆分[${count}镜]`;
}

export type LeadingShotSplitCommand = {
  command: string;
  count: ShotSplitCount;
  text: string;
};

export function splitLeadingShotSplitCommand(text: string): LeadingShotSplitCommand | null {
  const match = text.match(LEADING_SHOT_SPLIT_COMMAND_PATTERN);
  if (!match) return null;
  const parsed = match[1] ? Number(match[1]) : 2;
  if (!isShotSplitCount(parsed)) return null;
  return {
    command: formatShotSplitCommand(parsed),
    count: parsed,
    text: text.slice(match[0].length),
  };
}

export function joinShotSplitCommand(text: string, count: ShotSplitCount = 2): string {
  const command = formatShotSplitCommand(count);
  if (!text) return `${command} `;
  return `${command} ${text}`;
}

export function applyShotSplitCount(visibleText: string, count: ShotSplitCount): string {
  const current = splitLeadingShotSplitCommand(visibleText);
  return joinShotSplitCommand(current ? current.text : visibleText, count);
}

export function removeShotSplitCommand(visibleText: string): string {
  const current = splitLeadingShotSplitCommand(visibleText);
  return current ? current.text : visibleText;
}

const COMPOSER_DISPLAY_TOKEN = /@\[[^\]]+\]|\/拆分(?:\[[2-5]镜\])?(?=\s|$)/g;

export type ComposerDisplaySegment =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; token: string }
  | { kind: 'command'; token: string };

export function splitComposerDisplaySegments(text: string): ComposerDisplaySegment[] {
  const source = text ?? '';
  const segments: ComposerDisplaySegment[] = [];
  let cursor = 0;
  let sawToken = false;
  for (const match of source.matchAll(COMPOSER_DISPLAY_TOKEN)) {
    const index = match.index ?? 0;
    const token = match[0];
    const before = source.slice(cursor, index).trim();
    if (before) segments.push({ kind: 'text', text: before });
    sawToken = true;
    segments.push(token.startsWith('@') ? { kind: 'mention', token } : { kind: 'command', token });
    cursor = index + token.length;
  }
  const tail = source.slice(cursor).trim();
  if (!sawToken) return [{ kind: 'text', text: source }];
  if (tail) segments.push({ kind: 'text', text: tail });
  return segments;
}

export function composerTokenLabel(
  segment: Exclude<ComposerDisplaySegment, { kind: 'text' }>
): string {
  if (segment.kind === 'command') {
    const count = segment.token.match(/^\/拆分\[([2-5])镜\]$/)?.[1];
    return count ? `拆分[${count}镜]` : '拆分';
  }
  const match = segment.token.match(/^@\[([^:\]]+):([^\]]+)\]$/);
  if (!match) return segment.token;
  return `${match[1]} ${match[2]}`;
}

export function mentionNameFromToken(token: string): string {
  const match = token.trim().match(/^@\[([^:\]]+):([^\]]+)\]$/);
  return match ? match[2].trim() : '';
}

/** 正文保持用户输入；对象身份由显式选择的结构引用提供，不通过文本猜测增删引用。 */
export function resolveFollowUpSubmitContent(followUpText: string): string {
  return followUpText.trim();
}

export function buildAssetMentionSuggestions(
  assetResult: AISessionAssetResult | null,
  session?: CanonicalAISession | null
): FollowUpMentionSuggestion[] {
  if (!assetResult) return [];
  return ASSET_RESULT_GROUPS.flatMap((group) =>
    assetResult[group.key].map((item) => ({
      key: `${group.key}-${item.draftItemId ?? item.name}`,
      ...(session?.sessionType === 3 && item.draftItemId
        ? {
            reference: {
              sessionId: session.sessionId,
              artifactVersion: session.payload.assetVersion ?? 0,
              kind: group.key,
              entityId: item.draftItemId,
            },
          }
        : {}),
      label: `${group.label}: ${item.name}`,
      value: `@[${group.label}:${item.name}] `,
      description: item.description,
    }))
  );
}

export function buildStoryboardMentionSuggestions(
  session: CanonicalAISession | null
): FollowUpMentionSuggestion[] {
  if (session?.sessionType !== 4) return [];
  const storyboardResult = session?.payload?.storyboardResult;
  if (!storyboardResult || typeof storyboardResult !== 'object') return [];
  const storyboards = (storyboardResult as { storyboards?: unknown }).storyboards;
  if (!Array.isArray(storyboards)) return [];
  return storyboards.slice(0, 80).map((item, index) => {
    const record = item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
    const title =
      typeof record.title === 'string' && record.title.trim()
        ? record.title.trim()
        : `分镜 ${index + 1}`;
    const description =
      typeof record.description === 'string' && record.description.trim()
        ? record.description.trim()
        : undefined;
    return {
      key: `storyboard-${record.draftItemId ?? index + 1}`,
      ...(typeof record.draftItemId === 'string'
        ? {
            reference: {
              sessionId: session.sessionId,
              artifactVersion: session.payload.storyboardVersion ?? 0,
              kind: 'storyboards' as const,
              entityId: record.draftItemId,
            },
          }
        : {}),
      label: `镜号: ${index + 1}`,
      value: `@[镜号:${index + 1}] `,
      description: `${title}${description ? `：${description}` : ''}`,
    };
  });
}
